import { CIRCLE_COLORS, OUTSIDE_COLOR } from '../config';

export const VIEWBOX_W = 360;
export const VIEWBOX_H = 440;
export const RADIUS = 82;
export const VENN_ASPECT_RATIO = `${VIEWBOX_W} / ${VIEWBOX_H}`;

export const toPercent = (value: number, max: number): string => `${(value / max) * 100}%`;

const clampCircleCount = (count: number): number => Math.min(Math.max(count, 1), 3);

const CENTERS_BY_COUNT: Record<number, Matches.Point[]> = {
  1: [{ x: 180, y: 250 }],
  2: [
    { x: 120, y: 250 },
    { x: 240, y: 250 },
  ],
  3: [
    { x: 130, y: 175 },
    { x: 230, y: 175 },
    { x: 180, y: 258 },
  ],
};

export const getCenters = (count: number): Matches.Point[] =>
  CENTERS_BY_COUNT[clampCircleCount(count)];

const ANCHORS_3: Record<string, Matches.Point> = {
  '0': { x: 90, y: 190 },
  '1': { x: 270, y: 190 },
  '2': { x: 180, y: 312 },
  '0-1': { x: 180, y: 140 },
  '0-2': { x: 132, y: 248 },
  '1-2': { x: 228, y: 248 },
  '0-1-2': { x: 180, y: 218 },
  outside: { x: 322, y: 330 },
};

export type TitleAnchor = { x: number; y: number; below: boolean };

const TITLE_ANCHORS_BY_COUNT: Record<number, TitleAnchor[]> = {
  1: [{ x: 180, y: 150, below: false }],
  2: [
    { x: 96, y: 150, below: false },
    { x: 264, y: 150, below: false },
  ],
  3: [
    { x: 104, y: 84, below: false },
    { x: 256, y: 84, below: false },
    { x: 180, y: 350, below: true },
  ],
};

export const circleTitleAnchor = (index: number, count: number): TitleAnchor =>
  TITLE_ANCHORS_BY_COUNT[clampCircleCount(count)][index];

const centroid = (points: Matches.Point[]): Matches.Point => {
  if (!points.length) return { x: VIEWBOX_W / 2, y: VIEWBOX_H / 2 };
  const sum = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
  return { x: sum.x / points.length, y: sum.y / points.length };
};

const norm = (p: Matches.Point): Matches.Point => {
  const len = Math.hypot(p.x, p.y) || 1;
  return { x: p.x / len, y: p.y / len };
};

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

const regionKey = (setIndexes: number[]): string =>
  [...setIndexes].sort((a, b) => a - b).join('-') || 'outside';

export const regionAnchor = (setIndexes: number[], centers: Matches.Point[]): Matches.Point => {
  if (centers.length === 3) {
    return ANCHORS_3[regionKey(setIndexes)] ?? ANCHORS_3.outside;
  }
  const members = setIndexes.map(i => centers[i]).filter(Boolean);
  if (!members.length) return { x: VIEWBOX_W - 40, y: VIEWBOX_H - 80 };
  const m = centroid(members);
  const nonMembers = centers.filter((_, i) => !setIndexes.includes(i));
  if (!nonMembers.length) return m;
  const away = centroid(nonMembers);
  const dir = norm({ x: m.x - away.x, y: m.y - away.y });
  return { x: m.x + dir.x * RADIUS * 0.45, y: m.y + dir.y * RADIUS * 0.45 };
};

const scatter = (anchor: Matches.Point, indexInRegion: number, spread = 13): Matches.Point => {
  if (indexInRegion === 0) return anchor;
  const angle = indexInRegion * GOLDEN_ANGLE;
  const radius = spread * Math.sqrt(indexInRegion);
  return { x: anchor.x + Math.cos(angle) * radius, y: anchor.y + Math.sin(angle) * radius };
};

const hexToRgb = (hex: string): [number, number, number] => {
  const v = hex.replace('#', '');
  return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
};

const rgbToHex = (rgb: number[]): string =>
  '#' + rgb.map(c => Math.round(c).toString(16).padStart(2, '0')).join('');

export const regionColor = (setIndexes: number[]): string => {
  if (!setIndexes.length) return OUTSIDE_COLOR;
  const rgbs = setIndexes.map(i => hexToRgb(CIRCLE_COLORS[i]));
  const avg = [0, 1, 2].map(c => rgbs.reduce((sum, rgb) => sum + rgb[c], 0) / rgbs.length);
  return rgbToHex(avg);
};

export const layoutDots = (data: Matches.CategoryData): Matches.PositionedDot[] => {
  const centers = getCenters(data.circles.length);
  const counters: Record<string, number> = {};

  return data.assertions.map(assertion => {
    const key = regionKey(assertion.setIndexes);
    const indexInRegion = counters[key] ?? 0;
    counters[key] = indexInRegion + 1;
    const anchor = regionAnchor(assertion.setIndexes, centers);
    const { x, y } = scatter(anchor, indexInRegion);
    return { ...assertion, x, y, color: regionColor(assertion.setIndexes) };
  });
};

export const hasOutsideDots = (data: Matches.CategoryData): boolean =>
  data.assertions.some(assertion => assertion.setIndexes.length === 0);
